// Shared, pure helpers for the hierarchical view. Consumed by all three view
// modes (graph, chart, tree) so the tree-building and count logic lives in one
// place. The graph view keeps working off the flat `funcs` array; the chart and
// tree views use the nested tree + rollup counts produced here.

// One shared collator: String#localeCompare with options builds a new one on
// every call, which dominates sorting thousands of jobs.
const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

export const compareNames = (a, b) =>
  collator.compare(String(a || ""), String(b || ""));

export const jobName = (j) => (typeof j === "string" ? j : j?.name || "Job");

// Staff headcount for a job, or 0 when missing/invalid.
export const jobStaff = (j) => {
  const n = Number(j?.staff_count);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// A stable React key for a job within its function.
export const jobKey = (j, index) =>
  j && typeof j === "object" && (j.job_id ?? j.id) != null ?
    `j${j.job_id ?? j.id}`
  : `i${index}`;

// Lower-cased job names, computed once per job object.
const lowerNames = new WeakMap();
const lowerJobName = (j) => {
  if (!j || typeof j !== "object") return jobName(j).toLowerCase();
  let v = lowerNames.get(j);
  if (v === undefined) {
    v = jobName(j).toLowerCase();
    lowerNames.set(j, v);
  }
  return v;
};

export const jobMatches = (j, q) => lowerJobName(j).includes(q);

// Build a nested tree from the flat, normalized functions array.
// Each input function is expected to have: function_id, parent_id, name, jobs[].
// Returns { roots, byId } where every node also carries:
//   children: node[]        — direct child functions (name-sorted)
//   _subs:    number        — count of ALL descendant functions
//   _jobs:    number        — count of ALL jobs in the subtree
export function buildHierarchy(funcs) {
  const byId = new Map();

  (funcs || []).forEach((f) => {
    const id = Number(f?.function_id);
    if (!Number.isFinite(id)) return;
    byId.set(id, {
      ...f,
      function_id: id,
      parent_id:
        f?.parent_id == null || f?.parent_id === "" ? null : Number(f.parent_id),
      name: f?.name || "Function",
      jobs: Array.isArray(f?.jobs) ? f.jobs : [],
      children: [],
    });
  });

  const roots = [];
  byId.forEach((node) => {
    const pid = node.parent_id;
    if (pid != null && byId.has(pid)) {
      byId.get(pid).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortRec = (nodes) => {
    nodes.sort((a, b) => compareNames(a.name, b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);

  const count = (node) => {
    let subs = node.children.length;
    let jobs = node.jobs.length;
    node.children.forEach((c) => {
      const r = count(c);
      subs += r.subs;
      jobs += r.jobs;
    });
    node._subs = subs;
    node._jobs = jobs;
    return { subs, jobs };
  };
  roots.forEach(count);

  return { roots, byId };
}

// Walk parent links up from a function id to its root. Returns [root, ..., node].
export function pathToNode(byId, id) {
  const path = [];
  let cur = byId.get(Number(id));
  while (cur) {
    path.unshift(cur);
    const pid = cur.parent_id;
    cur = pid != null && byId.has(pid) ? byId.get(pid) : null;
  }
  return path;
}

// A short "4 sub · 23 jobs" summary of a node's subtree size.
export function countLabel(node) {
  if (!node) return "";
  const parts = [];
  if (node._subs > 0) parts.push(`${node._subs} sub`);
  if (node._jobs > 0) parts.push(`${node._jobs} job${node._jobs === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

// Search the whole hierarchy in one pass. Returns null for an empty query;
// otherwise a Map of function_id → { nameHit, jobs } for every function to
// show: those whose name matches, that hold matching jobs, or that lead to a
// match. `jobs` are the jobs to list (all of them when the name matches).
export function searchHierarchy(roots, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return null;
  const result = new Map();
  const visit = (node) => {
    const nameHit = String(node.name || "").toLowerCase().includes(q);
    const jobs = nameHit ? node.jobs : node.jobs.filter((j) => jobMatches(j, q));
    let childHit = false;
    node.children.forEach((c) => {
      if (visit(c)) childHit = true;
    });
    const hit = nameHit || jobs.length > 0 || childHit;
    if (hit) result.set(node.function_id, { nameHit, jobs });
    return hit;
  };
  (roots || []).forEach(visit);
  return result;
}

// Aggregate counts across a set of root nodes (used to summarize the whole
// company at the top of the chart/tree, where the company is a virtual root).
export function rollupRoots(roots = []) {
  return roots.reduce(
    (acc, n) => {
      acc.subs += 1 + (n._subs || 0);
      acc.jobs += n._jobs || 0;
      return acc;
    },
    { subs: 0, jobs: 0 },
  );
}
